import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserState } from './UserContext';
import { getEnrollmentHint, getMyEnrollments, MyEnrollment } from '../common/apiStudyUtilities';
import { debugLog } from '../common/debugUtilities';

/**
 * Study linkage state for the signed-in parent (cribsy-api docs/ENROLLMENT.md; decisions D18-D20):
 *  - enrollments: children this account has linked (GET /enrollments/mine)
 *  - pending: a claim is waiting for one of this account's contacts and nothing is linked yet (POST /enrollments/hint).
 *    This is what turns the dashboard banner and the upload gate on; families with no pending claim see nothing.
 *  - pendingCode: a code that arrived through a /claim/<code> link, kept across sign-in and registration until used.
 */
export type StudyState = {
  loaded: boolean;
  enrollments: MyEnrollment[];
  pending: boolean;
  pendingCode?: string;
  refresh: () => Promise<void>;
  setPendingCode: (code?: string) => void;
  isSubjectLinked: (subjectId?: string) => boolean;
};

const StudyContext = createContext<StudyState>(null!);

function StudyProvider({ children }: { children: React.ReactNode }) {
  const user = useUserState();
  const [loaded, setLoaded] = useState(false);
  const [enrollments, setEnrollments] = useState<MyEnrollment[]>([]);
  const [pending, setPending] = useState(false);
  const [pendingCode, setPendingCodeState] = useState<string | undefined>(undefined);
  const generation = useRef(0);

  const firebaseUser = user?.firebaseUser;
  const registered = !!user?.isAuthenticated && !!firebaseUser?.bearerToken && (user?.authLevel ?? -1) >= 0;

  const refresh = useCallback(async () => {
    const mine = ++generation.current;
    if (!registered || !firebaseUser) {
      setEnrollments([]); setPending(false); setLoaded(false);
      return;
    }
    const list = await getMyEnrollments(firebaseUser);
    const hint = list.length === 0 ? await getEnrollmentHint(firebaseUser) : false;
    if (mine !== generation.current) return; // a newer refresh superseded this one
    debugLog('[study] enrollments:', list.length, 'pending claim:', hint);
    setEnrollments(list);
    setPending(hint);
    setLoaded(true);
  }, [registered, firebaseUser, user?._id]);

  useEffect(() => { refresh(); }, [refresh]);

  const setPendingCode = useCallback((code?: string) => setPendingCodeState(code), []);
  const isSubjectLinked = useCallback(
    (subjectId?: string) => !!subjectId && enrollments.some(e => e.subjectId === subjectId),
    [enrollments],
  );

  const value = useMemo<StudyState>(() => ({ loaded, enrollments, pending, pendingCode, refresh, setPendingCode, isSubjectLinked }),
    [loaded, enrollments, pending, pendingCode, refresh, setPendingCode, isSubjectLinked]);

  return <StudyContext.Provider value={value}>{children}</StudyContext.Provider>;
}

function useStudy(): StudyState {
  const ctx = useContext(StudyContext);
  if (!ctx) throw new Error('useStudy must be used within a StudyProvider');
  return ctx;
}

export { StudyProvider, useStudy };
